import { useState, useEffect, useCallback, useMemo, type ReactNode, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { Plus, Pencil, Trash2, Loader2, Clock, Users } from "lucide-react";

interface ServiceRow {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly durationMinutes: number;
  readonly bufferMinutes: number;
  readonly priceAgorot: number;
  readonly maxParticipants: number;
  readonly bookingType: string;
  readonly requiresDeposit: number;
  readonly depositAmountAgorot: number | null;
  readonly cancellationHours: number;
  readonly isActive: number;
  readonly categoryId: string | null;
  readonly categoryName: string | null;
}

interface CategoryRow {
  readonly id: string;
  readonly name: string;
}

const BOOKING_TYPE_LABELS: Record<string, string> = {
  time_slot: "\u05EA\u05D5\u05E8",
  calendar_event: "\u05D0\u05D9\u05E8\u05D5\u05E2",
  recurring_class: "\u05E9\u05D9\u05E2\u05D5\u05E8",
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 240];

function formatNIS(agorot: number): string {
  return `\u20AA${(agorot / 100).toFixed(2)}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} \u05D3\u05E7\u05F3`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h} \u05E9\u05E2\u05D5\u05EA ${m} \u05D3\u05E7\u05F3` : `${h} \u05E9\u05E2\u05D5\u05EA`;
}

function ServiceForm({ service, categories, onSave, onCancel }: {
  readonly service: ServiceRow | null;
  readonly categories: readonly CategoryRow[];
  readonly onSave: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { accessToken } = useAuth();
  const isEdit = service !== null;

  const [name, setName] = useState(service?.name ?? "");
  const [description, setDescription] = useState(service?.description ?? "");
  const [durationMinutes, setDurationMinutes] = useState(String(service?.durationMinutes ?? 60));
  const [priceInput, setPriceInput] = useState(service ? String(service.priceAgorot / 100) : "");
  const [bookingType, setBookingType] = useState(service?.bookingType ?? "time_slot");
  const [categoryId, setCategoryId] = useState(service?.categoryId ?? "");
  const [maxParticipants, setMaxParticipants] = useState(String(service?.maxParticipants ?? 1));
  const [bufferMinutes, setBufferMinutes] = useState(String(service?.bufferMinutes ?? 0));
  const [cancellationHours, setCancellationHours] = useState(String(service?.cancellationHours ?? 24));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    const priceNIS = parseFloat(priceInput);
    if (isNaN(priceNIS) || priceNIS < 0) {
      setError("\u05DE\u05D7\u05D9\u05E8 \u05DC\u05D0 \u05EA\u05E7\u05D9\u05DF");
      setSaving(false);
      return;
    }

    const body = {
      name,
      description: description || undefined,
      duration_minutes: Number(durationMinutes),
      price_agorot: Math.round(priceNIS * 100),
      booking_type: bookingType,
      category_id: categoryId || undefined,
      max_participants: Number(maxParticipants),
      buffer_minutes: Number(bufferMinutes),
      cancellation_hours: Number(cancellationHours),
    };

    try {
      const url = isEdit ? `/api/booking/services/${service.id}` : "/api/booking/services";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });

      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) {
        setError(json.error ?? "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05D4");
        return;
      }

      onSave();
    } catch {
      setError("\u05E9\u05D2\u05D9\u05D0\u05EA \u05E8\u05E9\u05EA");
    } finally {
      setSaving(false);
    }
  }, [accessToken, isEdit, service, name, description, durationMinutes, priceInput, bookingType, categoryId, maxParticipants, bufferMinutes, cancellationHours, onSave]);

  return (
    <Card>
      <CardContent className="p-4 md:p-6">
        <h2 className="text-lg font-semibold mb-4">{isEdit ? "\u05E2\u05E8\u05D9\u05DB\u05EA \u05E9\u05D9\u05E8\u05D5\u05EA" : "\u05E9\u05D9\u05E8\u05D5\u05EA \u05D7\u05D3\u05E9"}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{"\u05E9\u05DD \u05E9\u05D9\u05E8\u05D5\u05EA"}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={3} maxLength={100} className="min-h-[44px]" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{"\u05EA\u05D9\u05D0\u05D5\u05E8"}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[44px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{"\u05DE\u05E9\u05DA (\u05D3\u05E7\u05D5\u05EA)"}</label>
              <select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[44px]"
              >
                {DURATION_OPTIONS.map((d) => (
                  <option key={d} value={d}>{formatDuration(d)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{"\u05DE\u05D7\u05D9\u05E8 (\u20AA)"}</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                required
                className="min-h-[44px]"
                dir="ltr"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{"\u05E1\u05D5\u05D2 \u05EA\u05D5\u05E8"}</label>
              <select
                value={bookingType}
                onChange={(e) => setBookingType(e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[44px]"
              >
                <option value="time_slot">{"\u05EA\u05D5\u05E8"}</option>
                <option value="calendar_event">{"\u05D0\u05D9\u05E8\u05D5\u05E2"}</option>
                <option value="recurring_class">{"\u05E9\u05D9\u05E2\u05D5\u05E8"}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{"\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4"}</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[44px]"
              >
                <option value="">{"\u05DC\u05DC\u05D0 \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4"}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{"\u05DE\u05E9\u05EA\u05EA\u05E4\u05D9\u05DD"}</label>
              <Input type="number" min="1" max="100" value={maxParticipants} onChange={(e) => setMaxParticipants(e.target.value)} className="min-h-[44px]" dir="ltr" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{"\u05D1\u05D0\u05E4\u05E8 (\u05D3\u05E7\u05F3)"}</label>
              <Input type="number" min="0" max="120" value={bufferMinutes} onChange={(e) => setBufferMinutes(e.target.value)} className="min-h-[44px]" dir="ltr" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{"\u05D1\u05D9\u05D8\u05D5\u05DC (\u05E9\u05E2\u05D5\u05EA)"}</label>
              <Input type="number" min="0" max="168" value={cancellationHours} onChange={(e) => setCancellationHours(e.target.value)} className="min-h-[44px]" dir="ltr" />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="min-h-[44px]">
              {saving && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {isEdit ? "\u05E9\u05DE\u05D5\u05E8" : "\u05D4\u05D5\u05E1\u05E3 \u05E9\u05D9\u05E8\u05D5\u05EA"}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} className="min-h-[44px]">{"\u05D1\u05D9\u05D8\u05D5\u05DC"}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function ServiceListPage(): ReactNode {
  const { accessToken } = useAuth();
  const [services, setServices] = useState<readonly ServiceRow[]>([]);
  const [categories, setCategories] = useState<readonly CategoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<ServiceRow | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const [servicesRes, categoriesRes] = await Promise.all([
        fetch("/api/booking/services", { headers: { Authorization: `Bearer ${accessToken}` } }),
        fetch("/api/booking/services/categories/list", { headers: { Authorization: `Bearer ${accessToken}` } }),
      ]);

      const servicesJson = (await servicesRes.json()) as { success: boolean; data: readonly ServiceRow[] };
      const categoriesJson = (await categoriesRes.json()) as { success: boolean; data: readonly CategoryRow[] };

      if (servicesJson.success) setServices(servicesJson.data);
      if (categoriesJson.success) setCategories(categoriesJson.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void fetchServices();
  }, [fetchServices]);

  const handleToggleActive = useCallback(async (service: ServiceRow) => {
    setTogglingId(service.id);
    try {
      await fetch(`/api/booking/services/${service.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ is_active: service.isActive ? 0 : 1 }),
      });
      await fetchServices();
    } finally {
      setTogglingId(null);
    }
  }, [accessToken, fetchServices]);

  const handleDelete = useCallback(async (id: string) => {
    await fetch(`/api/booking/services/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    await fetchServices();
  }, [accessToken, fetchServices]);

  const handleSave = useCallback(() => {
    setShowForm(false);
    setEditingService(null);
    void fetchServices();
  }, [fetchServices]);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingService(null);
  }, []);

  const handleEdit = useCallback((service: ServiceRow) => {
    setEditingService(service);
    setShowForm(true);
  }, []);

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; services: ServiceRow[] }>();
    const uncategorized: ServiceRow[] = [];

    for (const s of services) {
      if (s.categoryId && s.categoryName) {
        const group = map.get(s.categoryId);
        if (group) {
          group.services.push(s);
        } else {
          map.set(s.categoryId, { name: s.categoryName, services: [s] });
        }
      } else {
        uncategorized.push(s);
      }
    }

    return { categorized: Array.from(map.values()), uncategorized };
  }, [services]);

  if (showForm) {
    return (
      <div className="p-4 md:p-6">
        <ServiceForm service={editingService} categories={categories} onSave={handleSave} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
            <Clock className="h-4 w-4 text-primary" />
          </div>
          <h1 className="text-lg font-bold">{"\u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD"}</h1>
        </div>
        <Button onClick={() => { setEditingService(null); setShowForm(true); }} className="min-h-[44px] shadow-sm">
          <Plus className="h-4 w-4 me-2" />
          {"\u05D4\u05D5\u05E1\u05E3 \u05E9\u05D9\u05E8\u05D5\u05EA"}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-primary/50" />
        </div>
      ) : services.length === 0 ? (
        <Card className="border-dashed border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <Clock className="h-8 w-8 opacity-40" />
            </div>
            <p className="text-base font-medium">{"\u05E2\u05D3\u05D9\u05D9\u05DF \u05DC\u05D0 \u05D4\u05D5\u05E1\u05E4\u05EA \u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD"}</p>
            <p className="text-sm opacity-70 mt-1 mb-4">{"\u05D4\u05D5\u05E1\u05E3 \u05E9\u05D9\u05E8\u05D5\u05EA \u05E8\u05D0\u05E9\u05D5\u05DF \u05DB\u05D3\u05D9 \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC"}</p>
            <Button onClick={() => setShowForm(true)} className="min-h-[44px]">
              <Plus className="h-4 w-4 me-2" />
              {"\u05D4\u05D5\u05E1\u05E3 \u05E9\u05D9\u05E8\u05D5\u05EA \u05E8\u05D0\u05E9\u05D5\u05DF"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.categorized.map((group) => (
            <div key={group.name}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-border/50" />
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">{group.name}</h2>
                <div className="h-px flex-1 bg-border/50" />
              </div>
              <div className="space-y-2">
                {group.services.map((s) => (
                  <ServiceCard key={s.id} service={s} togglingId={togglingId} onToggle={handleToggleActive} onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          ))}

          {grouped.uncategorized.length > 0 && (
            <div>
              {grouped.categorized.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-border/50" />
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">{"\u05DC\u05DC\u05D0 \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4"}</h2>
                  <div className="h-px flex-1 bg-border/50" />
                </div>
              )}
              <div className="space-y-2">
                {grouped.uncategorized.map((s) => (
                  <ServiceCard key={s.id} service={s} togglingId={togglingId} onToggle={handleToggleActive} onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const BOOKING_TYPE_COLORS: Record<string, string> = {
  time_slot: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  calendar_event: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  recurring_class: "bg-amber-500/15 text-amber-400 border-amber-500/30",
};

function ServiceCard({ service, togglingId, onToggle, onEdit, onDelete }: {
  readonly service: ServiceRow;
  readonly togglingId: string | null;
  readonly onToggle: (s: ServiceRow) => void;
  readonly onEdit: (s: ServiceRow) => void;
  readonly onDelete: (id: string) => void;
}): ReactNode {
  return (
    <Card className={`transition-all duration-200 border-border/60 hover:border-border hover:shadow-sm ${service.isActive ? "" : "opacity-50"}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <span className="font-semibold text-base">{service.name}</span>
              <Badge className={`text-xs border ${BOOKING_TYPE_COLORS[service.bookingType] ?? "bg-muted text-muted-foreground border-border"}`}>
                {BOOKING_TYPE_LABELS[service.bookingType] ?? service.bookingType}
              </Badge>
              {!service.isActive && (
                <Badge variant="secondary" className="text-xs bg-zinc-500/15 text-zinc-400 border-zinc-500/30 border">
                  {"\u05DC\u05D0 \u05E4\u05E2\u05D9\u05DC"}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary/60" />
                {formatDuration(service.durationMinutes)}
              </span>
              <span className="font-semibold text-foreground">{formatNIS(service.priceAgorot)}</span>
              {service.maxParticipants > 1 && (
                <span className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-primary/60" />
                  {service.maxParticipants}
                </span>
              )}
            </div>
            {service.description && (
              <p className="text-xs text-muted-foreground mt-2 line-clamp-1 bg-muted/30 rounded-md px-2 py-1">
                {service.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggle(service)}
              disabled={togglingId === service.id}
              className="min-h-[44px] min-w-[44px]"
              title={service.isActive ? "\u05D4\u05E9\u05D1\u05EA" : "\u05D4\u05E4\u05E2\u05DC"}
            >
              <div className={`w-9 h-5 rounded-full relative transition-all duration-300 ${service.isActive ? "bg-primary shadow-sm shadow-primary/30" : "bg-muted"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-300 ${service.isActive ? "start-4" : "start-0.5"}`} />
              </div>
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onEdit(service)} className="min-h-[44px] min-w-[44px] hover:bg-primary/10 hover:text-primary transition-colors">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(service.id)} className="min-h-[44px] min-w-[44px] hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
